import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import ClassScheduleViewer from '../../components/ClassScheduleViewer';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  BookOpen, 
  Calendar, 
  CreditCard, 
  AlertCircle, 
  MessageSquare,
  Send,
  User,
  CheckCircle,
  Download,
  FileText,
  Award,
  Target,
  Clock,
  Video,
  Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { mockStudents, mockInvoices, mockProgressData } from '../../mockData/dashboardData';
import InvoiceDetailsModal from '../../components/modals/InvoiceDetailsModal';
import ProfileView from '../../components/ProfileView';
import TutorAvailabilityCardView from '../../components/TutorAvailabilityCardView';
import MessagesPage from './MessagesPage';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../supabaseClient';

// ============================================
// EXTRACTED COMPONENT - MessagesView (outside main component to prevent re-creation)
// ============================================
const MessagesView = React.memo(({ messageText, handleMessageTextChange }) => {
  const mockMessages = [
    {
      id: 1,
      from: 'Dr. Priya Sharma',
      role: 'Tutor',
      message: 'Rahul did excellent in today\'s session.',
      timestamp: '1 hour ago',
      unread: true
    },
    {
      id: 2,
      from: 'Admin Team',
      role: 'Admin',
      message: 'Your invoice for January has been generated.',
      timestamp: '1 day ago',
      unread: false
    }
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Messages</h2>
        <div className="space-y-3">
          {mockMessages.map((msg) => (
            <div
              key={msg.id}
              className={`border rounded-xl p-4 cursor-pointer hover:border-cyan-300 transition-colors ${
                msg.unread ? 'border-cyan-200 bg-cyan-50 dark:bg-gray-700' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-cyan-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{msg.from}</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{msg.role}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{msg.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{msg.timestamp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg flex flex-col h-[600px]">
        <div className="border-b border-gray-200 dark:border-gray-700 p-6">
          <h3 className="font-bold text-gray-900 dark:text-white">Dr. Priya Sharma</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Tutor</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-cyan-600" />
              </div>
              <div>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-4">
                  <p className="text-sm text-gray-900 dark:text-white">Rahul did excellent in today's session.</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">1 hour ago</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Type your message..."
              value={messageText}
              onChange={handleMessageTextChange}
              className="flex-1"
            />
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const ParentDashboard = () => {
  const { user, getAccessToken } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [messageText, setMessageText] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [children, setChildren] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [invoiceDetailsModalOpen, setInvoiceDetailsModalOpen] = useState(false);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [selectedTutorIdForAvailability, setSelectedTutorIdForAvailability] = useState(null);
  const [scheduleViewMode, setScheduleViewMode] = useState('class-schedule'); // 'class-schedule', 'tutor-availability', 'tasks'

  const pendingInvoices = invoices.filter(inv => inv.status === 'submitted' || inv.status === 'overdue');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  
  // Fetch tasks, schedules, and children from backend
  useEffect(() => {
    fetchTasks();
    fetchSchedules();
    fetchChildren();
    fetchInvoices();
    fetchTimesheets();

    // Set up real-time subscription
    const subscription = supabase
      .channel('parent_schedules_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_schedules'
        },
        (payload) => {
          console.log('Schedule change detected:', payload);
          fetchSchedules();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchTasks = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      // Parents should use /api/tasks endpoint to get children's tasks
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendTasks = await response.json();
        console.log('Fetched tasks:', backendTasks);
        setTasks(backendTasks);
      } else {
        console.warn('Failed to fetch tasks:', await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendSchedules = await response.json();
        console.log('Fetched schedules:', backendSchedules);
        setSchedules(backendSchedules);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const fetchChildren = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/my-children`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendChildren = await response.json();
        console.log('Fetched children:', backendChildren);
        
        // Transform children data to match the expected format
        const transformedChildren = backendChildren.map(child => ({
          id: child.id,
          name: child.full_name || child.email,
          email: child.email,
          avatar: child.avatar_url || 'https://via.placeholder.com/150',
          grade: 'Student', // We don't have grade info in users table
          subjects: [], // We'll calculate this from tasks
          progress: 0, // Mock for now
          sessionsCompleted: 0 // Mock for now
        }));
        
        setChildren(transformedChildren);
      } else {
        console.warn('Failed to fetch children:', await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
  };


  const fetchInvoices = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const invoicesData = await response.json();
        setInvoices(invoicesData.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      setInvoices([]);
    }
  };

  const fetchTimesheets = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;
      
      // Fetch approved timesheets for parent's tasks
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/timesheets?status_filter=approved&page=1&page_size=1000`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTimesheets(data.timesheets || []);
      }
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
    }
  };

  const handleViewInvoiceDetails = async (invoiceId) => {
    try {
      const token = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const details = await response.json();
        setSelectedInvoiceDetails(details);
        setInvoiceDetailsModalOpen(true);
      } else {
        alert('Failed to fetch invoice details');
      }
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      alert('Failed to fetch invoice details');
    }
  };
  
  // Stable callback handler to prevent input re-mounting
  const handleMessageTextChange = useCallback((e) => {
    setMessageText(e.target.value);
  }, []);
  
  const stats = [
    {
      label: 'Children Enrolled',
      value: children.length,
      icon: Users,
      color: 'from-blue-400 to-cyan-500'
    },
    {
      label: 'Total Sessions',
      value: children.reduce((acc, child) => acc + child.sessionsCompleted, 0),
      icon: BookOpen,
      color: 'from-green-400 to-emerald-500'
    },
    {
      label: 'Pending Payment',
      value: `₹${pendingInvoices.reduce((acc, inv) => acc + inv.finalAmount, 0)}`,
      icon: DollarSign,
      color: 'from-orange-400 to-red-500'
    },
    {
      label: 'Avg Progress',
      value: `${Math.round(children.reduce((acc, child) => acc + child.progress, 0) / children.length)}%`,
      icon: TrendingUp,
      color: 'from-purple-400 to-pink-500'
    }
  ];

  const mockMessages = [
    {
      id: 1,
      from: 'Dr. Priya Sharma',
      role: 'Math Tutor',
      message: 'Rahul is doing excellent in mathematics. Keep encouraging him!',
      timestamp: '3 hours ago',
      unread: true
    },
    {
      id: 2,
      from: 'Prof. Rajesh Verma',
      role: 'Physics Tutor',
      message: 'Ananya needs to practice more numerical problems.',
      timestamp: '1 day ago',
      unread: false
    }
  ];

  // Dashboard Overview
  const DashboardOverview = () => {
    // Filter today's classes - account for timezone conversion
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const todaysClasses = schedules.filter(schedule => {
      const scheduleDateTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
      const scheduleDateStr = `${scheduleDateTime.getFullYear()}-${String(scheduleDateTime.getMonth() + 1).padStart(2, '0')}-${String(scheduleDateTime.getDate()).padStart(2, '0')}`;
      return scheduleDateStr === todayStr;
    });

    // Function to check if Join Class button should be enabled
    const canJoinClass = (schedule) => {
      if (!schedule.meeting_link) return false;
      
      const now = new Date();
      const scheduleDateTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
      const classEndTime = new Date(scheduleDateTime.getTime() + schedule.duration_minutes * 60000);
      const tenMinsBefore = new Date(scheduleDateTime.getTime() - 10 * 60000);
      
      return now >= tenMinsBefore && now <= classEndTime;
    };

    return (
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Today's Classes for Children */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            Today's Classes
          </h2>
          <div className="space-y-4">
            {todaysClasses.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No classes scheduled for today</p>
              </div>
            ) : (
              todaysClasses.map((schedule) => {
                const task = tasks.find(t => t.id === schedule.task_id);
                const startTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
                const isJoinable = canJoinClass(schedule);
                
                return (
                  <div key={schedule.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white">{task?.title || 'Class'}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{task?.subject || 'Subject N/A'}</p>
                      </div>
                      <span className="px-3 py-1 bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300 text-xs font-semibold rounded-full">
                        Today
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Video className="w-4 h-4" />
                        <span>{schedule.duration_minutes} min</span>
                      </div>
                    </div>
                    {schedule.meeting_link ? (
                      <Button 
                        className={`w-full ${isJoinable ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700' : 'bg-gray-400 cursor-not-allowed'} text-white`}
                        onClick={() => isJoinable && window.open(schedule.meeting_link, '_blank')}
                        disabled={!isJoinable}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {isJoinable ? 'Join Class' : 'Not Yet Available'}
                      </Button>
                    ) : (
                      <Button className="w-full bg-gray-400 text-white cursor-not-allowed" disabled>
                        <Play className="w-4 h-4 mr-2" />
                        No Meeting Link
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Children Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            Your Children
          </h2>
          <div className="space-y-4">
            {children.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No children found</p>
              </div>
            ) : (
              children.map((child) => {
                // Get tasks for this child to extract subjects
                const childTasks = tasks.filter(t => t.student_id === child.id);
                const subjects = [...new Set(childTasks.map(t => t.subject))];
                
                return (
                  <div key={child.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-center gap-4 mb-4">
                      <img src={child.avatar} alt={child.name} className="w-12 h-12 rounded-full object-cover" />
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 dark:text-white">{child.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {child.grade} • {subjects.length > 0 ? subjects.join(', ') : 'No subjects assigned'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Enrolled Tasks</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{childTasks.length}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.min(100, childTasks.length * 20)}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // My Children View
  const MyChildrenView = () => (
    <div className="space-y-6">
      {children.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No children found</p>
          </div>
        </div>
      ) : (
        children.map((child) => {
          // Get tasks for this child
          const childTasks = tasks.filter(t => t.student_id === child.id);
          const subjects = [...new Set(childTasks.map(t => t.subject))];
          
          return (
            <div key={child.id} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <img src={child.avatar} alt={child.name} className="w-16 h-16 rounded-full object-cover" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{child.name}</h2>
                  <p className="text-gray-600 dark:text-gray-400">{child.email}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-cyan-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Enrolled Tasks</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{childTasks.length}</p>
                </div>
                <div className="bg-purple-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Active Subjects</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{subjects.length}</p>
                </div>
                <div className="bg-green-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Schedules</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {schedules.filter(s => s.student_id === child.id).length}
                  </p>
                </div>
              </div>

              {/* Tasks List */}
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-4">Enrolled Tasks</h3>
                {childTasks.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No tasks assigned yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {childTasks.map((task) => (
                      <div key={task.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">{task.title}</h4>
                          <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs font-semibold rounded">
                            {task.status}
                          </span>
                        </div>
                        <p className="text-sm text-cyan-600 dark:text-cyan-400 mb-2">{task.subject}</p>
                        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Billing Rate: ₹{task.billing_rate}/hour</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* AI Insights */}
      <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
        <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
          <Award className="w-6 h-6" />
          AI-Powered Insights
        </h3>
        <div className="space-y-3">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">📊 Rahul Kumar</p>
            <p className="text-sm text-purple-100">Shows 15% improvement in Math this month. Keep encouraging!</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">🎯 Ananya Patel</p>
            <p className="text-sm text-purple-100">Excelling in Physics - consider advanced topics.</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">💡 Recommendation</p>
            <p className="text-sm text-purple-100">Schedule extra Chemistry sessions for board prep.</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Billing View
  const BillingView = () => (
    <div className="space-y-6">
      {/* Billing Summary */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
          <h3 className="text-sm font-medium text-green-100 mb-2">Total Paid</h3>
          <p className="text-3xl font-bold">₹{paidInvoices.reduce((acc, inv) => acc + inv.finalAmount, 0).toLocaleString()}</p>
          <p className="text-sm text-green-100 mt-2">{paidInvoices.length} invoices</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 text-white">
          <h3 className="text-sm font-medium text-orange-100 mb-2">Pending Payment</h3>
          <p className="text-3xl font-bold">₹{pendingInvoices.reduce((acc, inv) => acc + inv.finalAmount, 0).toLocaleString()}</p>
          <p className="text-sm text-orange-100 mt-2">{pendingInvoices.length} invoices</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 text-white">
          <h3 className="text-sm font-medium text-purple-100 mb-2">Total Invoices</h3>
          <p className="text-3xl font-bold">{invoices.length}</p>
          <p className="text-sm text-purple-100 mt-2">All time</p>
        </div>
      </div>

      {/* All Invoices */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">All Invoices</h2>
          <Button variant="outline" className="text-cyan-600 border-cyan-600 hover:bg-cyan-50">
            <Download className="w-4 h-4 mr-2" />
            Download All
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Invoice ID</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Student</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Month</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Sessions</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Amount</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500 dark:text-gray-400">
                    No invoices available yet
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="py-3 px-4 font-medium">#{invoice.invoice_number}</td>
                    <td className="py-3 px-4">{children.length > 0 ? children[0].name : 'Child'}</td>
                    <td className="py-3 px-4">{new Date(invoice.period_start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</td>
                    <td className="py-3 px-4 text-right">-</td>
                    <td className="py-3 px-4 text-right font-bold text-gray-900 dark:text-white">₹{invoice.final_amount.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                        invoice.status === 'submitted' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" variant="outline" onClick={() => handleViewInvoiceDetails(invoice.id)}>
                          <FileText className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        {invoice.status === 'submitted' || invoice.status === 'overdue' ? (
                          <Button 
                            size="sm" 
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                            onClick={() => {
                              if (invoice.razorpay_short_url) {
                                window.open(invoice.razorpay_short_url, '_blank');
                              } else {
                                alert('Payment link not available. Please contact admin.');
                              }
                            }}
                          >
                            Pay Now
                          </Button>
                        ) : invoice.status === 'paid' ? (
                          <Button 
                            size="sm" 
                            disabled
                            className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 cursor-not-allowed"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Paid
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Schedule View - with tabs for different views
  const ScheduleView = () => {
    // Get unique tutor IDs from tasks
    const tutorIds = [...new Set(tasks.map(t => t.tutor_id).filter(Boolean))];
    
    // Update selected tutor when tasks load
    useEffect(() => {
      if (tutorIds.length > 0 && !selectedTutorIdForAvailability) {
        setSelectedTutorIdForAvailability(tutorIds[0]);
      }
    }, [tutorIds]);

    return (
      <div className="space-y-6">
        {/* View Mode Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setScheduleViewMode('class-schedule')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                scheduleViewMode === 'class-schedule'
                  ? 'bg-cyan-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Children Class Schedule
            </button>
            <button
              onClick={() => setScheduleViewMode('tutor-availability')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                scheduleViewMode === 'tutor-availability'
                  ? 'bg-cyan-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Clock className="w-4 h-4" />
              Tutor&apos;s Availability
            </button>
            <button
              onClick={() => setScheduleViewMode('tasks')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                scheduleViewMode === 'tasks'
                  ? 'bg-cyan-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Your Children&apos;s Tasks
            </button>
          </div>
        </div>

        {/* Class Schedule View */}
        {scheduleViewMode === 'class-schedule' && (
          <ClassScheduleViewer role="parent" />
        )}

        {/* Tutor Availability View */}
        {scheduleViewMode === 'tutor-availability' && (
          <div className="space-y-4">
            {tutorIds.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-lg text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Tutors Available</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  No tutors are assigned to your children yet.
                </p>
              </div>
            ) : (
              <>
                {/* Tutor Selector */}
                {tutorIds.length > 1 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Select Tutor to View Availability</h3>
                    <div className="flex flex-wrap gap-2">
                      {tasks.filter((t, i, arr) => 
                        arr.findIndex(task => task.tutor_id === t.tutor_id) === i
                      ).map(task => (
                        <Button
                          key={task.tutor_id}
                          variant={selectedTutorIdForAvailability === task.tutor_id ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedTutorIdForAvailability(task.tutor_id)}
                          className={selectedTutorIdForAvailability === task.tutor_id ? 
                            "bg-cyan-600 hover:bg-cyan-700 text-white" : 
                            ""}
                        >
                          {task.subject} Tutor
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Tutor Availability Display - Card View */}
                {selectedTutorIdForAvailability && (
                  <TutorAvailabilityCardView key={selectedTutorIdForAvailability} tutorId={selectedTutorIdForAvailability} />
                )}
              </>
            )}
          </div>
        )}

        {/* Children's Tasks View */}
        {scheduleViewMode === 'tasks' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              Your Children&apos;s Tasks
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.length === 0 ? (
                <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No tasks assigned yet</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className="border-2 border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white text-lg">{task.title}</h3>
                        <p className="text-sm text-cyan-600 dark:text-cyan-400 mt-1">{task.subject}</p>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs font-semibold rounded-full">
                        {task.status}
                      </span>
                    </div>
                    {task.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{task.description}</p>
                    )}
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Billing Rate</p>
                      <p className="text-lg font-bold text-blue-600">₹{task.billing_rate || 0}/hour</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Timesheets View
  const TimesheetsView = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <Clock className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
          Approved Timesheets
        </h2>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl p-4 text-white">
            <p className="text-sm text-blue-100 mb-1">Total Sessions</p>
            <p className="text-3xl font-bold">{timesheets.length}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
            <p className="text-sm text-green-100 mb-1">Total Hours</p>
            <p className="text-3xl font-bold">
              {timesheets.reduce((sum, ts) => sum + (ts.hours_worked || 0), 0).toFixed(1)}
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white">
            <p className="text-sm text-purple-100 mb-1">This Month</p>
            <p className="text-3xl font-bold">
              {timesheets.filter(ts => {
                const tsDate = new Date(ts.work_date);
                const now = new Date();
                return tsDate.getMonth() === now.getMonth() && tsDate.getFullYear() === now.getFullYear();
              }).length}
            </p>
          </div>
        </div>

        {/* Timesheets Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Date</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Task</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Description</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Hours</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
              </tr>
            </thead>
            <tbody>
              {timesheets.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500 dark:text-gray-400">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No approved timesheets yet</p>
                  </td>
                </tr>
              ) : (
                timesheets.map((timesheet) => {
                  // Find the task for this timesheet
                  const task = tasks.find(t => t.id === timesheet.task_id);
                  
                  return (
                    <tr key={timesheet.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4">
                        {new Date(timesheet.work_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {task?.title || 'Unknown Task'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {task?.subject || ''}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {timesheet.description || '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {timesheet.hours_worked}h
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                          {timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Progress Reports View
  const ProgressReportsView = () => (
    <div className="space-y-6">
      {children.map((child) => (
        <div key={child.id} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <img src={child.avatar} alt={child.name} className="w-12 h-12 rounded-full object-cover" />
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{child.name}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">{child.grade}</p>
              </div>
            </div>
            <Button variant="outline" className="text-cyan-600 border-cyan-600 hover:bg-cyan-50">
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </Button>
          </div>

          {/* Progress Chart */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">Progress Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockProgressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#0891b2" strokeWidth={3} dot={{ fill: '#0891b2', r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Subject Performance */}
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">Subject Performance</h3>
            <ResponsiveContainer width="100%" height={250}>
              <RechartsBarChart data={[
                { subject: 'Math', score: 88 },
                { subject: 'Science', score: 75 },
                { subject: 'English', score: 92 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="subject" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Bar dataKey="score" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <DashboardLayout role="parent" activeView={activeView} onViewChange={setActiveView}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeView === 'dashboard' && 'Parent Dashboard'}
            {activeView === 'children' && 'My Children'}
            {activeView === 'schedule' && "Children's Schedule"}
            {activeView === 'timesheets' && 'Timesheets'}
            {activeView === 'billing' && 'Billing'}
            {activeView === 'reports' && 'Progress Reports'}
            {activeView === 'messages' && 'Messages'}
            {activeView === 'profile' && 'My Profile'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {activeView === 'dashboard' && "Monitor your children's progress and manage payments"}
            {activeView === 'children' && "Track individual progress and performance"}
            {activeView === 'schedule' && "View all scheduled classes for your children"}
            {activeView === 'timesheets' && "View all approved timesheets for your children's sessions"}
            {activeView === 'billing' && "Manage payments and view invoices"}
            {activeView === 'reports' && "View detailed progress reports"}
            {activeView === 'messages' && "Communicate with tutors"}
            {activeView === 'profile' && 'Manage your profile information'}
          </p>
        </div>

        {/* Stats Grid - Show only on dashboard */}
        {activeView === 'dashboard' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Dynamic Content Based on Active View */}
        {activeView === 'dashboard' && <DashboardOverview />}
        {activeView === 'children' && <MyChildrenView />}
        {activeView === 'schedule' && <ScheduleView />}
        {activeView === 'timesheets' && <TimesheetsView />}
        {activeView === 'billing' && <BillingView />}
        {activeView === 'reports' && <ProgressReportsView />}
        {activeView === 'messages' && <MessagesPage />}
        {activeView === 'profile' && <ProfileView />}
      </div>

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        isOpen={invoiceDetailsModalOpen}
        onClose={() => {
          setInvoiceDetailsModalOpen(false);
          setSelectedInvoiceDetails(null);
        }}
        invoiceDetails={selectedInvoiceDetails}
      />
    </DashboardLayout>
  );
};

export default ParentDashboard;
